import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CheckCircle2, Loader2, PauseCircle, PlayCircle, Trash2 } from 'lucide-react';
import api from '@/lib/axios';
import { useSeo } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';

const DISTRITOS = ['Aveiro', 'Beja', 'Braga', 'Bragança', 'Castelo Branco', 'Coimbra', 'Évora', 'Faro',
  'Guarda', 'Leiria', 'Lisboa', 'Portalegre', 'Porto', 'Santarém', 'Setúbal', 'Viana do Castelo',
  'Vila Real', 'Viseu', 'Açores', 'Madeira'];

const ESPECIFICOS = {
  stand: [['marcas', 'Marcas comercializadas'], ['financiamento', 'Financiamento disponível'],
    ['retomas', 'Aceita retomas'], ['garantias', 'Garantias declaradas'], ['stock', 'Viaturas em stock']],
  associacao: [['regiao', 'Região'], ['missao', 'Missão'], ['servicos_associados', 'Serviços aos associados'],
    ['como_aderir', 'Como aderir'], ['quotas', 'Quotas'], ['comunicados', 'Comunicados ou eventos']],
  juridico: [['sociedade', 'Sociedade'], ['cedula', 'Cédula profissional (opcional)'],
    ['areas', 'Áreas de atuação'], ['consulta', 'Consulta presencial ou online']],
  oficina: [['especialidades', 'Especialidades'], ['marcas', 'Marcas atendidas'],
    ['urgente', 'Serviço urgente'], ['recolha', 'Recolha e entrega'], ['substituicao', 'Viatura de substituição']],
  default: [['publico', 'Público a que se destina'], ['detalhes', 'Outros detalhes úteis']],
};

const empty = {
  entity_type: 'stand', nome: '', descricao: '', servicos: '', distrito: 'Lisboa', concelho: '',
  localidade: '', zonas: '', telefone: '', email: '', whatsapp: '', website: '', instagram: '',
  facebook: '', horario: '', logotipo: '', fotos: [], atendimento: 'presencial',
  mostrar_telefone: true, mostrar_email: false, mostrar_whatsapp: false, responsavel_nome: '',
  especificos: {}, aceita_termos: false, aceita_privacidade: false,
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function MarketplacePartnerFormPage() {
  const [tipos, setTipos] = useState([]);
  const [form, setForm] = useState(empty);
  const [code, setCode] = useState('');
  const [token, setToken] = useState('');
  const [emailState, setEmailState] = useState({ sending: false, sent: false, verified: false, err: '' });
  const [state, setState] = useState({ loading: false, err: '', ok: '' });
  const [perfis, setPerfis] = useState(null);
  const [editing, setEditing] = useState(null);

  useSeo({ title: 'Adicionar o meu negócio ou entidade · Marketplace TVDE', description: 'Publique o seu stand, associação ou serviço TVDE no Marketplace.', path: '/adicionar-parceiro', noindex: true });

  React.useEffect(() => {
    api.get('/marketplace/parceiros/facetas').then((r) => setTipos(r.data.tipos || [])).catch(() => {});
  }, []);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const espec = ESPECIFICOS[form.entity_type] || ESPECIFICOS.default;

  const sendCode = async () => {
    setEmailState({ sending: true, sent: false, verified: false, err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email: form.email.trim() });
      setEmailState({ sending: false, sent: true, verified: false, err: '' });
    } catch (e) { setEmailState({ sending: false, sent: false, verified: false, err: e.response?.data?.detail || 'Falha ao enviar o código.' }); }
  };

  const verify = async () => {
    try {
      const res = await api.post('/marketplace/verificacao/confirmar', { email: form.email.trim(), code: code.trim() });
      setToken(res.data.verification_token);
      setEmailState((s) => ({ ...s, verified: true, err: '' }));
      loadPerfis(res.data.verification_token);
    } catch (e) { setEmailState((s) => ({ ...s, verified: false, err: e.response?.data?.detail || 'Código inválido.' })); }
  };

  const loadPerfis = async (tk = token) => {
    try {
      const res = await api.get(`/marketplace/parceiros/self?email=${encodeURIComponent(form.email.trim())}&verification_token=${tk}`);
      setPerfis(res.data.items || []);
    } catch (_) { setPerfis([]); }
  };

  const uploadFotos = async (files) => {
    const list = Array.from(files).slice(0, 6);
    const encoded = await Promise.all(list.map(fileToBase64));
    set({ fotos: encoded });
  };

  const submit = async (event) => {
    event.preventDefault();
    setState({ loading: true, err: '', ok: '' });
    const body = {
      ...form,
      servicos: form.servicos.split(',').map((s) => s.trim()).filter(Boolean),
      zonas: form.zonas.split(',').map((s) => s.trim()).filter(Boolean),
      email: form.email.trim(),
      verification_token: token,
    };
    try {
      if (editing) {
        await api.put(`/marketplace/parceiros/${editing}/self`, body);
        setState({ loading: false, err: '', ok: 'Perfil atualizado.' });
      } else {
        const res = await api.post('/marketplace/parceiros', body);
        setState({ loading: false, err: '', ok: res.data.mensagem });
      }
      setEditing(null);
      await loadPerfis();
    } catch (e) {
      setState({ loading: false, err: e.response?.data?.detail || 'Não foi possível guardar o perfil.', ok: '' });
    }
  };

  const acao = async (id, nome) => {
    try {
      await api.post(`/marketplace/parceiros/${id}/self/estado`, { email: form.email.trim(), verification_token: token, acao: nome });
      await loadPerfis();
    } catch (e) { setState((s) => ({ ...s, err: e.response?.data?.detail || 'Ação não aplicada.' })); }
  };

  const editar = (p) => {
    setEditing(p.id);
    setForm({
      ...empty, entity_type: p.entity_type, nome: p.nome, descricao: p.descricao,
      servicos: (p.servicos || []).join(', '), distrito: p.distrito, concelho: p.concelho || '',
      localidade: p.localidade || '', zonas: (p.zonas || []).join(', '),
      telefone: p.telefone || '', email: form.email, whatsapp: p.whatsapp || '',
      website: p.contacto?.website || '', instagram: p.contacto?.instagram || '',
      facebook: p.contacto?.facebook || '', horario: p.horario || '', logotipo: p.logotipo || '',
      fotos: p.fotos || [], atendimento: p.atendimento || 'presencial',
      mostrar_telefone: !!p.mostrar_telefone, mostrar_email: !!p.mostrar_email,
      mostrar_whatsapp: !!p.mostrar_whatsapp, responsavel_nome: p.responsavel_nome || '',
      especificos: p.especificos || {}, aceita_termos: true, aceita_privacidade: true,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const input = 'w-full rounded-xl border border-slate-300 px-3 py-2 text-sm';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" className="text-sm font-black text-slate-900">Marketplace <span className="text-emerald-600">TVDE</span></Link>
          <Link to="/parceiros" className="text-xs font-semibold text-slate-500" data-testid="partner-form-back">Ver parceiros</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 sm:text-5xl" data-testid="partner-form-title">
          {editing ? 'Editar perfil' : 'Adicionar o meu negócio ou entidade'}
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Sem contas nem passwords: confirmamos o seu email e o perfil segue para aprovação. Não pedimos documentos.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="partner-form">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h2 className="text-base font-bold text-slate-900">1. Categoria e identificação</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-600">Categoria
                <select value={form.entity_type} onChange={(e) => set({ entity_type: e.target.value, especificos: {} })}
                  className={input} data-testid="partner-form-type">
                  {tipos.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">Nome comercial ou profissional
                <input value={form.nome} onChange={(e) => set({ nome: e.target.value })} className={input} required data-testid="partner-form-name" />
              </label>
            </div>
            <label className="mt-3 block text-xs font-semibold text-slate-600">Descrição
              <textarea rows={4} value={form.descricao} onChange={(e) => set({ descricao: e.target.value })}
                className={input} required minLength={20} data-testid="partner-form-description" />
            </label>
            <label className="mt-3 block text-xs font-semibold text-slate-600">Serviços prestados (separados por vírgula)
              <input value={form.servicos} onChange={(e) => set({ servicos: e.target.value })} className={input} data-testid="partner-form-services" />
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h2 className="text-base font-bold text-slate-900">2. Localização e atendimento</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <label className="text-xs font-semibold text-slate-600">Distrito
                <select value={form.distrito} onChange={(e) => set({ distrito: e.target.value })} className={input} data-testid="partner-form-district">
                  {DISTRITOS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-600">Concelho
                <input value={form.concelho} onChange={(e) => set({ concelho: e.target.value })} className={input} data-testid="partner-form-county" />
              </label>
              <label className="text-xs font-semibold text-slate-600">Localidade
                <input value={form.localidade} onChange={(e) => set({ localidade: e.target.value })} className={input} data-testid="partner-form-locality" />
              </label>
            </div>
            <label className="mt-3 block text-xs font-semibold text-slate-600">Zonas de atuação (separadas por vírgula)
              <input value={form.zonas} onChange={(e) => set({ zonas: e.target.value })} className={input} data-testid="partner-form-zones" />
            </label>
            <label className="mt-3 block text-xs font-semibold text-slate-600">Atendimento
              <select value={form.atendimento} onChange={(e) => set({ atendimento: e.target.value })} className={input} data-testid="partner-form-service-mode">
                <option value="presencial">Presencial</option>
                <option value="online">Online</option>
                <option value="ambos">Presencial e online</option>
              </select>
            </label>
            <label className="mt-3 block text-xs font-semibold text-slate-600">Horário
              <input value={form.horario} onChange={(e) => set({ horario: e.target.value })} className={input} placeholder="Seg-Sex 9h-19h, Sáb 9h-13h" data-testid="partner-form-hours" />
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h2 className="text-base font-bold text-slate-900">3. Contactos</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-600">Telefone
                <input value={form.telefone} onChange={(e) => set({ telefone: e.target.value })} className={input} data-testid="partner-form-phone" />
              </label>
              <label className="text-xs font-semibold text-slate-600">WhatsApp (opcional)
                <input value={form.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} className={input} data-testid="partner-form-whatsapp" />
              </label>
              <label className="text-xs font-semibold text-slate-600">Website (opcional)
                <input value={form.website} onChange={(e) => set({ website: e.target.value })} className={input} data-testid="partner-form-website" />
              </label>
              <label className="text-xs font-semibold text-slate-600">Instagram ou Facebook (opcional)
                <input value={form.instagram} onChange={(e) => set({ instagram: e.target.value })} className={input} data-testid="partner-form-instagram" />
              </label>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
              <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.mostrar_telefone} onChange={(e) => set({ mostrar_telefone: e.target.checked })} data-testid="partner-form-show-phone" /> Mostrar telefone no perfil</label>
              <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.mostrar_email} onChange={(e) => set({ mostrar_email: e.target.checked })} data-testid="partner-form-show-email" /> Mostrar email no perfil</label>
              <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={form.mostrar_whatsapp} onChange={(e) => set({ mostrar_whatsapp: e.target.checked })} data-testid="partner-form-show-whatsapp" /> Mostrar WhatsApp</label>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h2 className="text-base font-bold text-slate-900">4. Informação da categoria</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {espec.map(([key, label]) => (
                <label key={key} className="text-xs font-semibold text-slate-600">{label}
                  <input value={form.especificos[key] || ''}
                    onChange={(e) => set({ especificos: { ...form.especificos, [key]: e.target.value } })}
                    className={input} data-testid={`partner-form-espec-${key}`} />
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h2 className="text-base font-bold text-slate-900">5. Logótipo e fotografias</h2>
            <label className="mt-3 block text-xs font-semibold text-slate-600">Logótipo
              <input type="file" accept="image/*" onChange={async (e) => { if (e.target.files?.[0]) set({ logotipo: await fileToBase64(e.target.files[0]) }); }}
                className="mt-1 block w-full text-xs" data-testid="partner-form-logo" />
            </label>
            <label className="mt-3 block text-xs font-semibold text-slate-600">Fotografias (até 6)
              <input type="file" accept="image/*" multiple onChange={(e) => e.target.files && uploadFotos(e.target.files)}
                className="mt-1 block w-full text-xs" data-testid="partner-form-photos" />
            </label>
            {form.fotos.length > 0 && <p className="mt-1 text-[11px] text-slate-500">{form.fotos.length} fotografia(s) selecionada(s)</p>}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <h2 className="text-base font-bold text-slate-900">6. Responsável e verificação</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-600">Nome do responsável (uso interno)
                <input value={form.responsavel_nome} onChange={(e) => set({ responsavel_nome: e.target.value })} className={input} required data-testid="partner-form-manager" />
              </label>
              <label className="text-xs font-semibold text-slate-600">Email do responsável
                <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className={input} required data-testid="partner-form-email" />
              </label>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={sendCode} disabled={emailState.sending || emailState.verified || !form.email.trim()}
                className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700 disabled:opacity-60" data-testid="partner-form-send-code">
                {emailState.verified ? '✓ Email verificado' : emailState.sending ? 'A enviar...' : emailState.sent ? 'Reenviar código' : 'Enviar código'}
              </button>
              {!emailState.verified && (
                <>
                  <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Código de 6 dígitos"
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="partner-form-code" />
                  <button type="button" onClick={verify} className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="partner-form-verify">Validar</button>
                </>
              )}
            </div>
            {emailState.err && <p className="mt-2 text-xs text-red-600" data-testid="partner-form-code-error">{emailState.err}</p>}
            <label className="mt-3 flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={form.aceita_termos} onChange={(e) => set({ aceita_termos: e.target.checked })} data-testid="partner-form-terms" />
              <span>Aceito os <Link to="/termos" target="_blank" className="font-semibold text-emerald-600 underline">Termos</Link> e confirmo que a informação é verdadeira.</span>
            </label>
            <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={form.aceita_privacidade} onChange={(e) => set({ aceita_privacidade: e.target.checked })} data-testid="partner-form-privacy" />
              <span>Li a <Link to="/privacidade" target="_blank" className="font-semibold text-emerald-600 underline">Política de Privacidade</Link>.</span>
            </label>
          </div>

          <button type="submit" disabled={state.loading || !token}
            className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
            data-testid="partner-form-submit">
            {state.loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : editing ? 'Guardar alterações' : 'Enviar para aprovação'}
          </button>
          {state.err && <p className="text-sm font-semibold text-red-600" data-testid="partner-form-error">{state.err}</p>}
          {state.ok && <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid="partner-form-ok"><CheckCircle2 className="h-4 w-4" /> {state.ok}</p>}
        </form>

        {perfis && (
          <section className="mt-10" data-testid="partner-self-list">
            <h2 className="text-base font-bold text-slate-900 md:text-lg">Os meus perfis ({perfis.length})</h2>
            {perfis.length === 0 && <p className="mt-1 text-xs text-slate-500">Ainda não tem perfis associados a este email.</p>}
            <ul className="mt-3 space-y-3">
              {perfis.map((p) => (
                <li key={p.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`partner-self-${p.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-bold text-slate-900">{p.nome}</p>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{p.tipo_label} · {p.estado}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 text-[11px] font-semibold">
                      <button type="button" onClick={() => editar(p)} className="rounded-lg border border-slate-300 px-2.5 py-1.5" data-testid={`partner-edit-${p.id}`}>Editar</button>
                      {p.estado === 'aprovado' && <button type="button" onClick={() => acao(p.id, 'pausar')} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5" data-testid={`partner-pause-${p.id}`}><PauseCircle className="h-3.5 w-3.5" /> Pausar</button>}
                      {p.estado === 'pausado' && <button type="button" onClick={() => acao(p.id, 'reativar')} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 px-2.5 py-1.5 text-emerald-700" data-testid={`partner-resume-${p.id}`}><PlayCircle className="h-3.5 w-3.5" /> Reativar</button>}
                      <button type="button" onClick={() => acao(p.id, 'pedir_eliminacao')} className="inline-flex items-center gap-1 rounded-lg border border-rose-300 px-2.5 py-1.5 text-rose-700" data-testid={`partner-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /> Pedir eliminação</button>
                    </div>
                  </div>
                  {p.motivo_rejeicao && <p className="mt-2 rounded-lg bg-rose-50 p-2 text-[11px] font-semibold text-rose-700" data-testid={`partner-reject-${p.id}`}>Rejeitado: {p.motivo_rejeicao}</p>}
                  {p.pedido_correcao && <p className="mt-2 rounded-lg bg-amber-50 p-2 text-[11px] font-semibold text-amber-800" data-testid={`partner-fix-${p.id}`}>Correção pedida: {p.pedido_correcao}</p>}
                  <p className="mt-2 text-[11px] text-slate-500" data-testid={`partner-stats-${p.id}`}>
                    Plano: {p.plano?.nome || 'sem plano'}{p.plano?.gratuito ? ' (gratuito)' : ''} · Visualizações: {p.estatisticas?.visualizacoes || 0} · Cliques no telefone: {p.estatisticas?.cliques_telefone || 0} · Conversas: {p.estatisticas?.conversas || 0}
                  </p>
                  {p.quota && (
                    <div className="mt-2 rounded-xl bg-slate-50 p-2" data-testid={`partner-quota-${p.id}`}>
                      <p className="text-[11px] font-bold text-slate-700">
                        <Building2 className="mr-1 inline h-3.5 w-3.5 text-emerald-600" />
                        Viaturas ativas: {p.quota.usado}{p.quota.limite === null ? ' (sem limite)' : ` de ${p.quota.limite} do plano`}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {(p.viaturas || []).slice(0, 12).map((v) => (
                          <li key={v.id} className="flex items-center justify-between text-[11px] text-slate-600">
                            <span className="truncate">{v.titulo}</span>
                            <span className="ml-2 shrink-0 uppercase text-slate-400">{v.status}</span>
                          </li>
                        ))}
                      </ul>
                      <Link to="/gerir-anuncio" className="mt-1 inline-block text-[11px] font-semibold text-emerald-700 underline" data-testid={`partner-manage-listings-${p.id}`}>Gerir cada anúncio</Link>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
      <MarketplaceFooter />
    </div>
  );
}
